import { Global } from "../../utils/Global";
import { Level, LevelType, OpponentLevel } from "../../utils/TimelineTypes";
import { BasicWindow } from "./BasicWindow";

export class LevelDescriptor extends BasicWindow {
  private titleText!: Phaser.GameObjects.BitmapText;
  private scoreRequiredText!: Phaser.GameObjects.BitmapText;
  private descriptionText!: Phaser.GameObjects.BitmapText;
  private levelIcon!: Phaser.GameObjects.Image;

  private currentLevel?: Level;
  private showDelay: number = 300; // ms delay before showing
  private showTimer?: Phaser.Time.TimerEvent;

  constructor(scene: Phaser.Scene) {
    super(
      scene,
      0, // x
      0, // y
      220, // width
      160, // height
      "Button1", // textureKey
      "", // frameKey
      220, // sliceWidth
      160, // sliceHeight
      7, // leftWidth
      7, // topHeight
      7, // rightWidth
      7, // bottomHeight
    );

    this.createContent();
    this.setDepth(25000); // Above items and level report, below pause menu
  }

  private createContent(): void {
    const contentContainer = this.contentContainer;

    // Create text elements with temporary positions (will be repositioned dynamically)
    // Level icon
    this.levelIcon = new Phaser.GameObjects.Image(
      this.scene,
      0,
      0,
      "icon_shop", // Default icon, will be updated
    );
    this.levelIcon.setOrigin(0.5);
    contentContainer.add(this.levelIcon);

    // Title
    this.titleText = new Phaser.GameObjects.BitmapText(
      this.scene,
      0,
      0,
      "fnt_paintBasic",
      "Level Name",
      Global.minFontSize,
    ).setOrigin(0.5);
    contentContainer.add(this.titleText);

    // Score Required (for opponents/bosses only)
    this.scoreRequiredText = new Phaser.GameObjects.BitmapText(
      this.scene,
      0,
      0,
      "fnt_paintBasic",
      "Score Required: 0",
      Global.minFontSize,
    ).setOrigin(0.5);
    this.scoreRequiredText.setVisible(false); // Hidden by default
    contentContainer.add(this.scoreRequiredText);

    // Description
    this.descriptionText = new Phaser.GameObjects.BitmapText(
      this.scene,
      0,
      0,
      "fnt_paintBasic",
      "No description available",
      Global.minFontSize,
    ).setOrigin(0.5);
    this.descriptionText.setCenterAlign();
    this.descriptionText.setTint(0xaaaaaa);
    contentContainer.add(this.descriptionText);
  }

  private updateContentAndResize(level: Level, onComplete?: () => void): void {
    // Reset all positions to 0,0 first to prevent accumulation
    this.titleText.setPosition(0, 0);
    this.scoreRequiredText.setPosition(0, 0);
    this.descriptionText.setPosition(0, 0);
    this.levelIcon.setPosition(0, 0);

    // Position content starting from top, let resizeToFitContent handle centering
    const fontSize = Global.minFontSize;
    const lineSpacing = fontSize * 0.3;
    const iconSize = fontSize * 2; // Icon size

    let currentY = 0; // Start from top, will be centered later

    // Icon (top)
    if (level.icon && level.icon !== "") {
      this.levelIcon.setTexture(level.icon);
    }
    this.levelIcon.setDisplaySize(iconSize, iconSize);
    this.levelIcon.setPosition(0, currentY);
    currentY += iconSize + lineSpacing;

    // Title (level name)
    this.titleText.setText(level.name || "Unnamed Level");
    this.titleText.setFontSize(fontSize * 1.2);
    this.titleText.setPosition(0, currentY);
    currentY += fontSize * 1.2 + lineSpacing;

    // Score Required (only for opponents and bosses)
    if (
      level.levelType === LevelType.OPPONENT ||
      level.levelType === LevelType.BOSS
    ) {
      const opponentLevel = level as OpponentLevel;
      if (opponentLevel.requiredScore !== undefined) {
        this.scoreRequiredText.setText(
          `Score Required: ${opponentLevel.requiredScore}`,
        );
        this.scoreRequiredText.setFontSize(fontSize);
        this.scoreRequiredText.setOrigin(0.5, 0.5);
        this.scoreRequiredText.setPosition(0, currentY);
        this.scoreRequiredText.setVisible(true);
        currentY += fontSize + lineSpacing;
      } else {
        this.scoreRequiredText.setVisible(false);
      }
    } else {
      this.scoreRequiredText.setVisible(false);
    }

    // Description
    this.descriptionText.setText(
      level.description || "No description available",
    );
    this.descriptionText.setFontSize(fontSize);
    this.descriptionText.setOrigin(0.5, 0);
    this.descriptionText.setPosition(0, currentY);

    this.resizeToFitContent();
    // Call the completion callback after resizing is done
    if (onComplete) {
      onComplete();
    }
  }

  public showForLevel(level: Level, mouseX: number, mouseY: number): void {
    // Clear any existing timers
    this.clearTimers();

    this.currentLevel = level;

    // Set up show timer
    this.showTimer = this.scene.time.delayedCall(this.showDelay, () => {
      if (this.currentLevel === level) {
        // Update content but don't show yet
        this.updateContentAndResize(level, () => {
          // Only show after content is properly sized and positioned
          this.positionNearMouse(mouseX, mouseY);
          this.show();
        });
      }
    });
  }

  public hideDescriptor(): void {
    // Clear any existing timers
    this.clearTimers();

    this.hide();
    this.currentLevel = undefined;
  }

  public hideDescriptorImmediately(): void {
    // Clear any existing timers
    this.clearTimers();

    // Hide immediately without delay
    this.hide();
    this.currentLevel = undefined;
  }

  private clearTimers(): void {
    if (this.showTimer) {
      this.showTimer.destroy();
      this.showTimer = undefined;
    }
  }

  private positionNearMouse(mouseX: number, mouseY: number): void {
    const padding = Global.minFontSize;
    const windowBounds = this.getBounds();
    const windowWidth = windowBounds.width;
    const windowHeight = windowBounds.height;
    const screenWidth = this.scene.scale.width;
    const screenHeight = this.scene.scale.height;

    // Default position: to the right and slightly below the mouse
    let x = mouseX + windowWidth / 2 + padding;
    let y = mouseY;

    // Check horizontal positioning
    if (x + windowWidth / 2 > screenWidth) {
      // Window would go off the right edge, position to the left of mouse
      x = mouseX - windowWidth / 2 - padding;
    }

    // Check vertical positioning
    if (y + windowHeight / 2 > screenHeight) {
      // Window would go off the bottom edge, position above mouse
      y = mouseY - windowHeight / 2 - padding;
    } else if (y - windowHeight / 2 < 0) {
      // Window would go off the top edge, position below mouse
      y = mouseY + windowHeight / 2 + padding;
    }

    // Final boundary checks to ensure window stays fully on screen
    x = Math.max(windowWidth / 2, Math.min(screenWidth - windowWidth / 2, x));
    y = Math.max(
      windowHeight / 2,
      Math.min(screenHeight - windowHeight / 2, y),
    );

    this.setPosition(x, y);
  }

  public override destroy(): void {
    this.clearTimers();
    super.destroy();
  }
}
